import { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { Send, X, Sparkles, ChevronUp, BookOpen, Calculator, FileText, AlertTriangle } from "lucide-react";

const QUICK_PROMPTS = [
  { icon: BookOpen, label: "IFRS guidance", prompt: "What IFRS standards apply to my asset register?" },
  { icon: Calculator, label: "Depreciation help", prompt: "Explain how my monthly depreciation is calculated" },
  { icon: FileText, label: "Tax rules", prompt: "What SARS sections apply to my asset categories?" },
  { icon: AlertTriangle, label: "Latest tax updates", prompt: "What are the latest SARS tax law changes for 2025/2026 that affect fixed assets?" },
];

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 px-4 py-3">
      <div className="flex gap-1">
        {[0, 1, 2].map(i => (
          <div key={i} className="w-2 h-2 rounded-full bg-[#C8A961]" style={{
            animation: `typingBounce 1.4s ease-in-out ${i * 0.16}s infinite`
          }} />
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ role, text, isLatest }) {
  const isUser = role === "user";

  // Simple markdown-like formatting for AI responses
  const formatText = (t) => {
    if (isUser) return t;
    // Bold
    let formatted = t.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Inline code / standard refs
    formatted = formatted.replace(/`(.*?)`/g, '<code class="px-1 py-0.5 rounded bg-[#0B1020]/5 text-[#0B1020] font-mono text-[11px]">$1</code>');
    // Bullet points
    formatted = formatted.replace(/^[-•] (.+)$/gm, '<div class="flex gap-2 items-start"><span class="text-[#C8A961] mt-0.5">-</span><span>$1</span></div>');
    // Standard references (IAS XX.XX, IFRS XX, Section XX)
    formatted = formatted.replace(/(IAS \d+\.\d+[-\d]*|IAS \d+|IFRS \d+\.\d+|IFRS \d+|IFRIC \d+|Section \d+\w*\(?[a-z]?\)?|S\d+[A-Z]?)/g,
      '<span class="inline-flex items-center px-1.5 py-0.5 rounded bg-[#C8A961]/10 text-[#0B1020] font-mono text-[10px] font-semibold tracking-wide">$1</span>');
    return formatted;
  };

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} ${isLatest ? "animate-slideIn" : ""}`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#0B1020] to-[#1A233A] flex items-center justify-center mr-2 mt-1 flex-shrink-0 shadow-sm">
          <Sparkles size={13} className="text-[#C8A961]" />
        </div>
      )}
      <div className={`max-w-[82%] ${isUser
        ? "bg-[#0B1020] text-white rounded-[20px] rounded-br-[4px] px-4 py-2.5 shadow-sm"
        : "bg-white text-[#1A1A1A] rounded-[20px] rounded-bl-[4px] px-4 py-3 shadow-sm border border-[#E8E2D5]/50"
      }`}>
        {isUser ? (
          <p className="text-[13.5px] leading-relaxed">{text}</p>
        ) : (
          <div className="text-[13.5px] leading-[1.65] space-y-2 [&_strong]:font-semibold [&_strong]:text-[#0B1020]"
            dangerouslySetInnerHTML={{ __html: formatText(text) }} />
        )}
      </div>
    </div>
  );
}

export default function AIChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showQuick, setShowQuick] = useState(true);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  const sendMessage = async (text) => {
    const q = (text || input).trim();
    if (!q || loading) return;
    setInput("");
    setShowQuick(false);
    setMessages(prev => [...prev, { role: "user", text: q, ts: Date.now() }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { question: q });
      const prefix = data.web_search_used ? "🌐 *Live web search used for latest information*\n\n" : "";
      setMessages(prev => [...prev, { role: "assistant", text: prefix + data.answer, ts: Date.now(), webSearch: data.web_search_used }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", text: "I'm having trouble connecting right now. Please try again in a moment.", ts: Date.now() }]);
    } finally {
      setLoading(false);
    }
  };

  // Floating trigger button
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 group"
        data-testid="ai-chat-toggle"
      >
        <div className="relative">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#0B1020] to-[#1A233A] shadow-lg shadow-[#0B1020]/25 flex items-center justify-center transition-all duration-300 group-hover:shadow-xl group-hover:shadow-[#0B1020]/30 group-hover:scale-105">
            <Sparkles size={22} className="text-[#C8A961] transition-transform duration-300 group-hover:rotate-12" />
          </div>
          <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#C8A961] border-2 border-[#F8F5EF] animate-pulse" />
        </div>
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-6 right-6 z-50 w-[380px] h-[580px] flex flex-col overflow-hidden shadow-2xl shadow-[#0B1020]/20"
      style={{ borderRadius: "24px", animation: "chatOpen 0.35s cubic-bezier(0.16, 1, 0.3, 1)" }}
      data-testid="ai-chat-panel"
    >
      {/* Header — frosted glass */}
      <div className="bg-[#0B1020] px-5 py-4 flex items-center justify-between relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1020] via-[#1A233A] to-[#0B1020]" />
        <div className="relative flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#C8A961] to-[#A08840] flex items-center justify-center shadow-lg shadow-[#C8A961]/20">
            <Sparkles size={17} className="text-white" />
          </div>
          <div>
            <h3 className="text-white text-sm font-semibold tracking-tight">Stratum AI</h3>
            <p className="text-white/40 text-[10px] tracking-wide">IFRS Expert Assistant</p>
          </div>
        </div>
        <button onClick={() => setOpen(false)} className="relative w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" data-testid="ai-chat-close">
          <X size={15} className="text-white/70" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto bg-[#F8F5EF] px-4 py-4 space-y-4"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>

        {/* Welcome state */}
        {messages.length === 0 && (
          <div className="text-center py-6 animate-fadeIn">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#0B1020] to-[#1A233A] flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Sparkles size={22} className="text-[#C8A961]" />
            </div>
            <h4 className="font-display text-base font-semibold text-[#0B1020] mb-1">Ask me anything</h4>
            <p className="text-xs text-[#6B7280] max-w-[260px] mx-auto leading-relaxed">
              I know your assets, IFRS standards, SARS tax rules, and SA GAAP. Ask in plain English.
            </p>
          </div>
        )}

        {/* Quick prompts */}
        {messages.length === 0 && showQuick && (
          <div className="space-y-2 animate-fadeIn" style={{ animationDelay: "0.15s" }}>
            {QUICK_PROMPTS.map((qp, i) => (
              <button key={i} onClick={() => sendMessage(qp.prompt)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border border-[#E8E2D5]/60 hover:border-[#C8A961]/40 hover:shadow-sm transition-all text-left group"
                data-testid={`quick-prompt-${i}`}
                style={{ animationDelay: `${0.1 + i * 0.05}s` }}>
                <div className="w-8 h-8 rounded-full bg-[#FAF8F4] flex items-center justify-center group-hover:bg-[#C8A961]/10 transition-colors">
                  <qp.icon size={15} className="text-[#C8A961]" strokeWidth={1.5} />
                </div>
                <span className="text-[13px] text-[#1A1A1A] font-medium">{qp.label}</span>
                <ChevronUp size={13} className="ml-auto text-[#E8E2D5] rotate-90 group-hover:text-[#C8A961] transition-colors" />
              </button>
            ))}
          </div>
        )}

        {/* Message bubbles */}
        {messages.map((m, i) => (
          <MessageBubble key={m.ts || i} role={m.role} text={m.text} isLatest={i === messages.length - 1} />
        ))}

        {loading && <TypingIndicator />}
        <div ref={bottomRef} />
      </div>

      {/* Input — iOS-style */}
      <div className="bg-[#F8F5EF] px-3 pb-4 pt-2">
        <div className="flex items-center gap-2 bg-white rounded-full border border-[#E8E2D5] pl-4 pr-1.5 py-1.5 shadow-sm focus-within:border-[#C8A961]/50 focus-within:shadow-md transition-all">
          <input
            ref={inputRef}
            className="flex-1 text-[13.5px] text-[#1A1A1A] placeholder-[#6B7280]/50 outline-none bg-transparent"
            placeholder="Ask about assets, IFRS, tax..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && sendMessage()}
            data-testid="ai-chat-input"
          />
          <button
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
              input.trim() && !loading
                ? "bg-[#0B1020] text-[#C8A961] shadow-sm hover:bg-[#1A233A] scale-100"
                : "bg-[#E8E2D5]/50 text-[#6B7280]/30 scale-95"
            }`}
            data-testid="ai-chat-send"
          >
            <Send size={14} className={input.trim() ? "" : ""} style={{ transform: "rotate(-45deg)", marginLeft: "1px", marginBottom: "1px" }} />
          </button>
        </div>
        <p className="text-center text-[9px] text-[#6B7280]/40 mt-2 tracking-wide">Powered by Claude with full IFRS & SARS knowledge</p>
      </div>

      {/* Inline CSS for animations */}
      <style>{`
        @keyframes chatOpen {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes typingBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-6px); opacity: 1; }
        }
        .animate-slideIn { animation: slideIn 0.3s ease-out; }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out both; }
      `}</style>
    </div>
  );
}
